import { Button, Empty, Form, message, Modal } from "antd";
import { Appraisal, AppraisalEntry, AppraisalFindRequest } from "../type";
import { AppraisalForm } from "./AppraisalForm";
import { AppraisalPageContext } from "./AppraisalPageContext";
import React, { useEffect, useMemo, useState } from "react";
import {
  BadRequestException,
  EntryMode,
  PageableRequest,
  PageContextValue,
  QueryPageableRequest,
  QueryPageableResponse,
} from "../../../core/type";
import { confirm, notify, translate } from "../../../core";
import {
  Container,
  ErrorMessage,
  SinglePageable,
  SinglePageableResponse,
  StimulsoftViewer,
} from "../../../core/component";
import {
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveFilled,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { AppraisalSearch } from "./AppraisalSearch";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useAppraisalData } from "../hook/appraisal-data-hook";
import { AppraisalSendEmail, GetAppraisalForPage, GetAppraisalPage, } from "../graphql/appraisal.graphql";
import AppraisalReport from "./AppraisalReport";
import { SendEmail } from "../../../core/component/SendEmail";
import styles from "./AppraisalReport.module.scss";
import { uniqueRandomString } from '../../../core/until'
import { useNavigate, useParams } from "react-router";

export const AppraisalPage = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [entity, setEntity] = useState<Appraisal>();
  const [page, setPage] = useState<number | null>();
  const [errors, setErrors] = useState<string[]>([]);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [findArgs, setFindArgs] = useState<AppraisalFindRequest>();
  const [entryMode, setEntryMode] = useState<EntryMode>(EntryMode.Nothing);
  const [form] = Form.useForm<AppraisalEntry>();
  const [find] = useLazyQuery<
    QueryPageableResponse<Appraisal, "appraisalsFind">,
    QueryPageableRequest<AppraisalFindRequest>
  >(GetAppraisalForPage);
  const { init, toDto, ...actions } = useAppraisalData();
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [isRefetch, setIsRefetch] = useState<boolean>(false);
  const [printOpen, setPrintOpen] = useState<boolean>();
  const [printKey, setPrintKey] = useState<string>(uniqueRandomString())
  const { number } = useParams<{ number?: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Appraisal | null>(null);
  const [sendEmail, { loading: sendingEmail }] = useMutation(
    AppraisalSendEmail,
    {
      onCompleted: () => {
        setOpenSendEmail(false);
        messageApi.success(translate("email", "sent"));
      },
      onError: (error) => messageApi.error(error.message),
    }
  );
  const [messageApi, contextHolder] = message.useMessage();
  const [openSendEmail, setOpenSendEmail] = useState<boolean>(false);

  useQuery<{ appraisalPage: number }, { number: string }>(GetAppraisalPage, {
    variables: {
      number: number ?? "",
    },
    skip: !number,
    onCompleted: (dataPage) => {
      if (data?.number !== number) {
        fetch({}, dataPage.appraisalPage)
      }
    },
  });

  useEffect(() => {
    setIsFormChanged(false);
  }, [entity]);

  const save = async (entry: AppraisalEntry) => {
    setSending(true);

    setErrors([]);
    const dto = toDto(entry);
    const id = entity?.id;

    try {
      if (id) await actions.update(id, dto);
      else {
        const result = await actions.create(dto);
        if (entity) entity.id = result.id;
      }

      notify.success(translate("appraisal"), translate("save_success_message"));

      await refresh();
    } catch (e) {
      if (e instanceof BadRequestException) setErrors(e.message);
    } finally {
      setSending(false);
      setIsRefetch(true);
    }
  };

  const remove = async () => {
    if (!entity) return;

    const result = await confirm(
      translate("remove_confirmation_message", "appraisal", "?")
    );

    if (!result) return;

    await actions.delete(entity.id);

    notify.success(
      translate("remove", "appraisal"),
      translate("the", "appraisal", "remove_success_message")
    );

    close();
  };

  const refresh = async () => {
    const { data } = await find({
      variables: {
        request: {
          take: 1,
          skip: 0,
          id: entity?.id,
        },
      },
    });
    const [result] = data?.appraisalsFind.data ?? [];
    setEntity(result);
    setEntryMode(EntryMode.Modify);
    setPrintKey(uniqueRandomString())
  };

  const onChange = async (
    request: PageableRequest,
    page: number
  ): Promise<SinglePageableResponse<Appraisal>> => {
    setLoading(true);

    try {
      const { data } = await find({
        variables: {
          request: {
            take: request.take || 1,
            skip: request.skip || 0,
            ...findArgs,
          },
        },
      });

      const [entity] = data?.appraisalsFind.data ?? [];
      navigate(`/appraisal/${entity?.number}`);
      setData(entity);

      if (!entity) {
        setEntity(undefined);
      }
      setEntity(entity);
      setEntryMode(EntryMode.Modify);

      if (!page) setPage(1);

      return { count: data?.appraisalsFind.count ?? 0 };
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setEntity(undefined);
    setEntryMode(EntryMode.Nothing);
  };

  const fetch = (findArgs: AppraisalFindRequest, page?: number) => {
    setFindArgs(findArgs);
    setPage(0);
    setTimeout(() => setPage(page), 500);
  };

  const value = useMemo<PageContextValue<any, any>>(
    () => ({
      form,
      save,
      entity,
      fetch,
      refresh,
      onFormChange: () => setIsFormChanged(true),
    }),
    [save, entity, fetch, refresh]
  );

  return (
    <AppraisalPageContext.Provider value={value}>
      <Container
        leftClassName="action-container"
        paddingClass="0"
        loading={loading}
        toolbarItems={{
          start: [
            <div key="search-add-group" className="button-group">
              <Button
                onClick={() => setSearchOpen(true)}
                icon={<SearchOutlined />}
                key={translate("search")}
                className="btn-primary-gradient"
              >
                {translate("search")}
              </Button>

              <Button
                onClick={async () => {
                  setEntryMode(EntryMode.New);
                  setLoading(true);
                  try {
                    setEntity(await init());
                  } finally {
                    setLoading(false);
                  }
                }}
                icon={<PlusOutlined />}
                key={translate("add")}
                className="btn-primary-gradient"
              >
                {translate("add")}
              </Button>
            </div>,

            <div key="save-remove-clear-group" className="button-group">
              <Button
                loading={sending}
                onClick={() => form.submit()}
                disabled={!entity}
                type={isFormChanged ? "primary" : "default"}
                icon={<SaveFilled />}
                key={translate("save")}
              >
                {translate("save")}
              </Button>
              <Button
                danger
                disabled={!entity?.id}
                onClick={remove}
                icon={<DeleteOutlined />}
                key={translate("remove")}
              >
                {translate("remove")}
              </Button>
              <Button
                disabled={entryMode === EntryMode.Nothing}
                onClick={async () => {
                  if (entryMode === EntryMode.New) {
                    form.resetFields();
                    setIsFormChanged(false);
                  }
                  if (entryMode === EntryMode.Modify) await refresh();
                }}
                icon={<StopOutlined />}
                key={translate("clear")}
              >
                {translate("clear")}
              </Button>
            </div>,

            <div key="actions-group" className="button-group">
              <Button
                disabled={!entity?.id}
                onClick={() => setPrintOpen(true)}
                key={translate("report")}
                icon={<PrinterOutlined />}
              >
                {translate("report", "/", "email")}
              </Button>
            </div>,

            <div key="exit-group" className="button-group">
              <Button
                onClick={close}
                icon={<CloseOutlined />}
                key={translate("exit")}
              >
                {translate("exit")}
              </Button>
            </div>,
          ],
          end: [
            <SinglePageable
              key={translate("pagination")}
              onChange={onChange}
              defaultPage={page}
            />,
          ],
        }}
      >
        <AppraisalSearch
          open={searchOpen}
          closeMe={() => setSearchOpen(false)}
        />

        {entity ? (
          <>
            <div
              className={[
                "print-hidden mt-2 bg-gray",
                styles["orderEntryForm"],
              ].join(" ")}
            >
              <ErrorMessage
                title={translate("entry_fail_message", "appraisal")}
                message={errors}
              />
              <AppraisalForm />
            </div>
            <AppraisalReport
              id={entity?.id}
              isRefetch={isRefetch}
              fetched={() => setIsRefetch(false)}
            />
          </>
        ) : (
          <Empty className="mt-5" />
        )}

        <Modal
          closable
          open={printOpen}
          footer={[]}
          width="60%"
          onCancel={() => setPrintOpen(false)}
        >
          <div className="p-1">
            <StimulsoftViewer
              key={printKey}
              cartStyle={{ width: "unset" }}
              name="appraisal-print"
              request={{ id: entity?.id }}
              email={{
                defaultEmailAddress: entity?.customerEmail,
              }}
              skip={!entity?.id}
            />
          </div>
        </Modal>
      </Container>

      <SendEmail
        attachment={{
          enabled: true,
          prefix: "appraisal-attachment",
        }}
        open={openSendEmail}
        onCancel={() => setOpenSendEmail(false)}
        onSend={(data) =>
          sendEmail({
            variables: {
              request: {
                id: entity?.id ?? 0,
                ...data,
              },
            },
          })
        }
        sending={sendingEmail}
      />
      {contextHolder}
    </AppraisalPageContext.Provider>
  );
};
